<template>
  <div>
    <v-alert v-if="error" type="error">
      {{ error }}
    </v-alert>
    <template v-else>
      <v-card outlined rounded="lg" class="mt-4">
        <v-card-text class="d-flex flex-wrap justify-space-around align-center">
          <div class="text-center d-flex flex-column align-center px-4">
            <v-skeleton-loader
              v-if="loading"
              width="50"
              height="20"
              class="mb-1"
              type="card"
            />
            <h6 v-else class="mb-0">
              {{ resume.eventCost | currency }}
            </h6>
            <span class="text-caption">Custo para o evento</span>
          </div>
          <div class="text-center d-flex flex-column align-center px-4">
            <v-skeleton-loader
              v-if="loading"
              width="50"
              height="20"
              class="mb-1"
              type="card"
            />
            <h6 v-else class="mb-0">
              {{ resume.companyCost | currency }}
            </h6>
            <span class="text-caption">Custo para a empresa</span>
          </div>
          <div class="text-center d-flex flex-column align-center px-4">
            <v-skeleton-loader
              v-if="loading"
              width="50"
              height="20"
              class="mb-1"
              type="card"
            />
            <h6 v-else class="mb-0">
              {{ resume.totalConfirmedAmount | currency }}
            </h6>
            <span v-if="resume.totalUnconfirmedAmount > 0">
              {{ resume.totalUnconfirmedAmount | currency }} não confirmados
            </span>

            <span class="text-caption">Total</span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <p class="mx-4 mt-3 font-weight-bold mb-0">Custos por categoria</p>
        <v-list dense>
          <v-list-item v-for="type in resume.types" :key="type.type">
            <v-list-item-avatar class="my-0 mr-2">
              <v-icon>{{ types[type.type].icon }}</v-icon>
            </v-list-item-avatar>
            <v-list-item-content class="py-1">
              <v-list-item-title class="font-weight-bold">
                {{ types[type.type].name }}
              </v-list-item-title>
              <v-progress-linear
                :value="
                  (type.confirmedAmount / resume.totalConfirmedAmount) * 100
                "
                rounded
                style="max-width: 500px"
                color="primary"
                class="mt-1"
                :height="18"
              >
                <template v-slot:default="{ value }">
                  {{ (value / 100) | percentage(0) }}
                </template>
              </v-progress-linear>
            </v-list-item-content>
            <v-list-item-action class="my-0">
              <b>
                {{ type.confirmedAmount | currency }}
              </b>
              <v-list-item-subtitle v-if="type.unconfirmedAmount > 0">
                {{ type.unconfirmedAmount | currency }} não confirmados
              </v-list-item-subtitle>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card>

      <v-data-table
        class="mt-4"
        :headers="headers"
        :items="mappedResourceRequests"
        :loading="loading"
        :items-per-page="10"
      >
        <template v-slot:item.items.length="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-chip color="primary" small v-bind="attrs" v-on="on">
                {{ item.items.length }}
                <v-icon x-small right>mdi-eye</v-icon>
              </v-chip>
            </template>
            <div class="d-flex flex-column">
              <p v-for="item in item.items" :key="item.id" class="mb-0">
                <span class="font-weight-bold">{{ types[item.type].name }}: </span>
                <span>{{ item.amount | currency }}</span>
              </p>
            </div>
          </v-tooltip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="statuses[item.status].color"
            small
            class="white--text"
            label
          >
            {{ statuses[item.status].text }}
          </v-chip>
        </template>
        <template v-slot:item.totalAmount="{ item }">
          {{ item.totalAmount | currency }}
        </template>
        <template v-slot:item.eventCost="{ item }">
          {{ item.eventCost | currency }}
        </template>
        <template v-slot:item.companyCost="{ item }">
          {{ item.companyCost | currency }}
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="hasPermission(16777216)"
            icon
            :to="{
              name: 'admin.resource-request.management',
              params: {
                id: item.id,
              },
            }"
          >
            <v-icon small>mdi-eye</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PARTY from "@/services/admin/parties";
import { status } from "@/definitions/ResourceRequest.json";

const context = require.context("../resourceRequest/types", true, /\.vue$/);
const types = context.keys().reduce((acc, key) => {
  const { manifest, default: component } = context(key);
  acc[manifest.id] = {
    ...manifest,
    component,
  };
  return acc;
}, {});

export default {
  name: "PartyResourceRequest",
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      statuses: status,
      types,
      loading: true,
      error: null,
      resourceRequests: [],
      headers: [
        { text: "#", value: "code" },
        { text: "Itens", value: "items.length" },
        { text: "Status", value: "status" },
        { text: "Custo Evento", value: "eventCost" },
        { text: "Custo Empresa", value: "companyCost" },
        { text: "Custo Total", value: "totalAmount" },
        { text: "Ações", value: "actions" },
      ],
    };
  },
  mounted() {
    this.loadResourceRequests();
  },
  methods: {
    async loadResourceRequests() {
      try {
        const response = await PARTY.resourceRequests(this.party.id);
        this.resourceRequests = response.resourceRequests;
      } catch (error) {
        this.error = error;
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    ...mapGetters("auth", ["hasPermission"]),
    mappedResourceRequests() {
      return this.resourceRequests.map((request) => {
        const totalAmount = request.items.reduce((acc, item) => {
          return acc + item.amount;
        }, 0);

        let eventCost = null;
        let companyCost = null;

        if (["APPROVED", "PAID"].includes(request.status)) {
          eventCost = request.partyTotalDebit;
          companyCost = totalAmount - eventCost;
        }

        return {
          ...request,
          totalAmount,
          eventCost,
          companyCost,
        };
      });
    },
    resume() {
      const resume = this.mappedResourceRequests.reduce(
        (acc, resourceRequest) => {
          const { totalAmount, eventCost, companyCost } = resourceRequest;

          let unconfirmedAmount = 0;
          if (["SUBMITTED"].includes(resourceRequest.status)) {
            unconfirmedAmount = totalAmount;
          }

          for (const item of resourceRequest.items) {
            const { type } = item;

            if (!acc.types[type])
              acc.types[type] = {
                totalAmount: 0,
                confirmedAmount: 0,
                unconfirmedAmount: 0,
              };

            acc.types[type].totalAmount += item.amount;
            acc.types[type].confirmedAmount += item.amount - unconfirmedAmount;
            acc.types[type].unconfirmedAmount += unconfirmedAmount;
          }

          return {
            ...acc,
            totalAmount: acc.totalAmount + totalAmount,
            totalConfirmedAmount:
              acc.totalConfirmedAmount + totalAmount - unconfirmedAmount,
            totalUnconfirmedAmount:
              acc.totalUnconfirmedAmount + unconfirmedAmount,
            eventCost: acc.eventCost + eventCost,
            companyCost: acc.companyCost + companyCost,
          };
        },
        {
          totalAmount: 0,
          totalConfirmedAmount: 0,
          totalUnconfirmedAmount: 0,
          eventCost: 0,
          companyCost: 0,
          types: {},
        }
      );

      // map types to array of objects and sort by totalAmount
      resume.types = Object.entries(resume.types)
        .map(([type, value]) => ({
          type,
          ...value,
        }))
        .sort((a, b) => b.totalAmount - a.totalAmount);

      return resume;
    },
  },
};
</script>
